import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PUBLIC_CONTAINER, whatsappHref } from './publicShell'
import WhatsAppLink from './WhatsAppLink'

const HEADING_ID = 'pricing-heading'

/**
 * Gradiente do plano em destaque. Mesmos tokens do banner do app, para os dois
 * blocos azuis da página serem reconhecidamente o mesmo azul.
 */
const HIGHLIGHT_GRADIENT =
  'linear-gradient(90deg, var(--color-lp-blue, #012FAE) 0%, var(--color-lp-blue-deep, #1847AC) 100%)'

interface Plan {
  id: string
  name: string
  price: string
  /** Período, separado do preço para não competir com ele no tamanho da fonte. */
  period: string
  capacity: string
  description: string
  /** Só um plano leva o selo — dois "mais popular" não querem dizer nada. */
  highlight?: string
}

const PLANS: Plan[] = [
  {
    id: 'surf-start',
    name: 'Plano Surf Start',
    price: 'R$ 187',
    period: '/mês',
    capacity: 'Até 50 alunos',
    description:
      'Ideal para quem está começando a digitalizar a escola ou possui uma operação mais enxuta e personalizada.',
  },
  {
    id: 'surf-evolution',
    name: 'Plano Surf Evolution',
    price: 'R$ 247',
    period: '/mês',
    capacity: 'Até 120 alunos',
    description:
      'O plano intermediário para escolas que já estão crescendo e precisam de consistência na organização.',
    highlight: 'Mais popular',
  },
  {
    id: 'big-rider',
    name: 'Plano Big Rider',
    price: 'R$ 387',
    period: '/mês',
    capacity: 'Até 250 alunos',
    description:
      'Para operações de grande escala que dominam o litoral e precisam de gestão total para centenas de alunos.',
  },
]

/**
 * TODO: o WhatsApp é o destino provisório. Quando existir checkout, troca aqui.
 */
function planHref(plan: Plan): string {
  return whatsappHref(`Olá! Tenho interesse no ${plan.name} da BeWave.`)
}

/**
 * "Planos": os três níveis de assinatura da plataforma.
 *
 * O do meio vem em destaque — fundo azul e selo —, que é como a grade conta qual é
 * a escolha esperada sem precisar dizer.
 */
export default function PricingPlans() {
  return (
    <section className="w-full py-12 lg:py-20" aria-labelledby={HEADING_ID}>
      <div className={PUBLIC_CONTAINER}>
        <div className="flex flex-col items-center text-center">
          <span className="text-[0.6875rem] font-bold uppercase tracking-[0.13em] text-lp-blue">
            Planos
          </span>
          <h2
            id={HEADING_ID}
            className="mt-3 max-w-[24ch] text-[1.75rem] font-extrabold leading-[1.3] text-gray-900 sm:text-3xl lg:text-[2.5rem]"
          >
            Escolha o plano ideal para sua escola.
          </h2>
        </div>

        {/* `items-stretch` (padrão do grid) + `h-full` no card: as descrições têm
            tamanhos diferentes, e sem isso os três cards terminariam em alturas
            diferentes com os botões desalinhados. */}
        <ul className="mx-auto mt-10 grid max-w-[76rem] gap-6 lg:mt-14 lg:grid-cols-3">
          {PLANS.map((plan) => (
            <li key={plan.id}>
              <PlanCard plan={plan} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function PlanCard({ plan }: { plan: Plan }) {
  const featured = Boolean(plan.highlight)

  return (
    <div
      className={cn(
        'flex h-full flex-col rounded-[2rem] p-8 lg:p-10',
        featured
          ? 'text-white'
          : 'border border-gray-200 bg-white text-gray-900',
      )}
      style={featured ? { background: HIGHLIGHT_GRADIENT } : undefined}
    >
      {/* Ocupa a linha mesmo sem selo, para preço e nome ficarem na mesma altura
          nos três cards em vez de o do meio descer sozinho. */}
      <div className="mb-6 h-7">
        {plan.highlight && (
          <span className="inline-flex rounded-full bg-white/20 px-3 py-1 text-[0.6875rem] font-bold uppercase tracking-[0.13em] text-white">
            {plan.highlight}
          </span>
        )}
      </div>

      <h3
        className={cn(
          'text-lg font-bold',
          featured ? 'text-white' : 'text-gray-900',
        )}
      >
        {plan.name}
      </h3>

      <p className="mt-3 flex items-baseline gap-1">
        <span
          className={cn(
            'text-[2.25rem] font-extrabold leading-none tracking-tight',
            featured ? 'text-white' : 'text-lp-blue',
          )}
        >
          {plan.price}
        </span>
        <span
          className={cn(
            'text-base font-semibold',
            featured ? 'text-white/80' : 'text-gray-500',
          )}
        >
          {plan.period}
        </span>
      </p>

      <p
        className={cn(
          'mt-2 text-sm font-semibold',
          featured ? 'text-white/90' : 'text-gray-700',
        )}
      >
        {plan.capacity}
      </p>

      <p
        className={cn(
          'mt-6 flex-1 text-[0.9375rem] leading-relaxed',
          featured ? 'text-white/80' : 'text-gray-600',
        )}
      >
        {plan.description}
      </p>

      {/* A descrição leva o `flex-1`, então o botão desce para o rodapé do card:
          com textos de tamanhos diferentes, é o que mantém os três alinhados. */}
      <WhatsAppLink
        href={planHref(plan)}
        source="pricing_plans"
        listingType="bewave"
        className={cn(
          'mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[0.9375rem] font-semibold transition',
          featured
            ? 'bg-white text-lp-blue hover:bg-gray-50'
            : 'bg-lp-blue text-white hover:opacity-90',
        )}
      >
        Quero este plano
        <ArrowRight className="size-4" aria-hidden="true" />
      </WhatsAppLink>
    </div>
  )
}
